import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bot, Send, Loader2, Check, SkipForward, ChevronRight, Sparkles, Building2, ShoppingBag, Settings, Mic, Square, Volume2, VolumeX } from 'lucide-react';
import { workerApi } from '../../services/workerApi';
import { useVoiceRecorder } from '../../hooks/useVoiceRecorder';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
type SectionKey = 'business_info' | 'products' | 'bot_config';

interface Section {
  key: SectionKey;
  label: string;
  icon: React.ReactNode;
  description: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface OnboardingInterviewProps {
  channelId: string;
  onComplete: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Section definitions
// ─────────────────────────────────────────────────────────────────────────────
const SECTIONS: Section[] = [
  {
    key: 'business_info',
    label: 'Thông tin doanh nghiệp',
    icon: <Building2 size={16} />,
    description: 'Tên, lĩnh vực, mô tả, liên hệ',
  },
  {
    key: 'products',
    label: 'Sản phẩm & Dịch vụ',
    icon: <ShoppingBag size={16} />,
    description: 'Sản phẩm, giá, chính sách',
  },
  {
    key: 'bot_config',
    label: 'Cấu hình Chatbot',
    icon: <Settings size={16} />,
    description: 'Lời chào, giọng nói, quy tắc',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const OnboardingInterview: React.FC<OnboardingInterviewProps> = ({ channelId, onComplete }) => {
  const [sectionIdx, setSectionIdx] = useState(0);
  const [completedSections, setCompletedSections] = useState<Set<SectionKey>>(new Set());
  const [history, setHistory] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [transcribing, setTranscribing] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const [speakingIdx, setSpeakingIdx] = useState<number | null>(null);
  const [voices, setVoices] = useState<{ id: string; name: string; lang: string }[]>([]);
  const [selectedVoiceURI, setSelectedVoiceURI] = useState<string>('vi-VN-HoaiMyNeural');

  const { isRecording, audioLevel, startRecording, stopRecording, error: micError } = useVoiceRecorder();

  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const startedSectionRef = useRef<string | null>(null);

  const currentSection = SECTIONS[sectionIdx];
  const isAllDone = completedSections.size === SECTIONS.length;

  // ── TTS helpers ────────────────────────────────────────────────────────

  // Load Edge TTS voices from worker
  useEffect(() => {
    workerApi.getTTSVoices()
      .then(res => { if (res.voices?.length) setVoices(res.voices); })
      .catch(() => {});
  }, []);

  const stopSpeaking = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (audioUrlRef.current) { URL.revokeObjectURL(audioUrlRef.current); audioUrlRef.current = null; }
    setSpeakingIdx(null);
  }, []);

  const speakText = useCallback(async (text: string, idx?: number) => {
    stopSpeaking();
    const cleaned = text.replace(/\*\*/g, '').replace(/[#*_~`]/g, '').trim();
    if (!cleaned) return;
    if (idx !== undefined) setSpeakingIdx(idx);
    try {
      const blob = await workerApi.textToSpeech(cleaned, selectedVoiceURI);
      const url = URL.createObjectURL(blob);
      audioUrlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setSpeakingIdx(null); URL.revokeObjectURL(url); audioUrlRef.current = null; audioRef.current = null; };
      audio.onerror = () => { setSpeakingIdx(null); URL.revokeObjectURL(url); audioUrlRef.current = null; audioRef.current = null; };
      audio.play();
    } catch {
      setSpeakingIdx(null);
    }
  }, [selectedVoiceURI, stopSpeaking]);

  // Stop speaking on unmount or section change
  useEffect(() => {
    return () => {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      if (audioUrlRef.current) { URL.revokeObjectURL(audioUrlRef.current); audioUrlRef.current = null; }
    };
  }, [sectionIdx]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history, loading]);

  // Focus input
  useEffect(() => {
    if (!loading) inputRef.current?.focus();
  }, [loading, sectionIdx]);

  // Start section — send initial message to AI
  const startSection = useCallback(async () => {
    setHistory([]);
    setInput('');
    setLoading(true);
    setError('');
    try {
      const res = await workerApi.interviewOnboarding(
        'Bắt đầu phỏng vấn',
        [],
        currentSection.key,
      );
      setHistory([{ role: 'assistant', content: res.reply }]);
      if (ttsEnabled) speakText(res.reply, 0);
    } catch {
      const fallback = 'Xin chào! Hãy cho tôi biết thông tin nhé.';
      setHistory([{ role: 'assistant', content: fallback }]);
      if (ttsEnabled) speakText(fallback, 0);
    } finally {
      setLoading(false);
    }
  }, [currentSection.key]);

  // Start first section on mount, and whenever sectionIdx changes
  useEffect(() => {
    if (!completedSections.has(currentSection.key) && startedSectionRef.current !== currentSection.key) {
      startedSectionRef.current = currentSection.key;
      startSection();
    }
  }, [sectionIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save section data ──────────────────────────────────────────────────
  const saveSectionData = async (data: {
    section_data?: Record<string, any>;
    items?: any[];
    products?: any[];
  }) => {
    setSaving(true);
    try {
      // Save bot config fields
      if (data.section_data && Object.keys(data.section_data).length > 0) {
        await workerApi.patchBotConfig(channelId, data.section_data);
      }

      // Save KB items
      if (data.items?.length) {
        await workerApi.bulkImportKB(data.items, channelId);
      }

      // Save products into bot config
      if (data.products?.length) {
        try {
          const cfg: any = await workerApi.getBotConfig(channelId);
          const existing = cfg.products || [];
          const merged = [...existing, ...data.products];
          await workerApi.patchBotConfig(channelId, { products: merged });
        } catch {
          // If getBotConfig fails, just set products directly
          await workerApi.patchBotConfig(channelId, { products: data.products });
        }
      }
    } catch (e) {
      console.error('Failed to save section data:', e);
    } finally {
      setSaving(false);
    }
  };

  // ── Send message ────────────────────────────────────────────────────────
  const sendMessage = async () => {
    const msg = input.trim();
    if (!msg || loading) return;

    const newHistory = [...history, { role: 'user' as const, content: msg }];
    setHistory(newHistory);
    setInput('');
    setLoading(true);
    setError('');

    try {
      const res = await workerApi.interviewOnboarding(msg, newHistory, currentSection.key);

      if (res.done) {
        // Section complete — save data
        const doneText = res.reply || 'Đã hoàn thành!';
        setHistory(h => {
          const next = [...h, { role: 'assistant' as const, content: doneText }];
          if (ttsEnabled) speakText(doneText, next.length - 1);
          return next;
        });
        await saveSectionData({
          section_data: res.section_data,
          items: res.items,
          products: res.products,
        });

        // Mark section as completed
        setCompletedSections(prev => {
          const next = new Set(prev);
          next.add(currentSection.key);
          return next;
        });

        // Auto-advance after short delay
        setTimeout(() => {
          if (sectionIdx + 1 < SECTIONS.length) {
            setSectionIdx(sectionIdx + 1);
          }
        }, 1500);
      } else {
        setHistory(h => {
          const next = [...h, { role: 'assistant' as const, content: res.reply }];
          if (ttsEnabled) speakText(res.reply, next.length - 1);
          return next;
        });
      }
    } catch (e: any) {
      const errMsg = e?.response?.data?.detail || 'Có lỗi xảy ra. Thử lại nhé.';
      setError(errMsg);
      // Remove the failed user message from display to allow retry
      setHistory(newHistory.slice(0, -1));
      setInput(msg);
    } finally {
      setLoading(false);
    }
  };

  // ── Skip section ────────────────────────────────────────────────────────
  const skipSection = () => {
    stopSpeaking();
    setCompletedSections(prev => {
      const next = new Set(prev);
      next.add(currentSection.key);
      return next;
    });
    if (sectionIdx + 1 < SECTIONS.length) {
      setSectionIdx(sectionIdx + 1);
    }
  };

  // ── Skip all remaining ─────────────────────────────────────────────────
  const skipAll = () => {
    stopSpeaking();
    onComplete();
  };

  // ── Handle Enter / Ctrl+Enter ──────────────────────────────────────────
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.ctrlKey && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
    // Ctrl+Enter inserts a newline (default browser behavior for textarea)
  };

  // ── Auto-resize textarea ───────────────────────────────────────────────
  const autoResize = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  };

  // ── Voice recording handler ─────────────────────────────────────────
  const handleVoiceToggle = async () => {
    if (isRecording) {
      const blob = await stopRecording();
      if (!blob) return;
      setTranscribing(true);
      setError('');
      try {
        const res = await workerApi.transcribeVoice(blob);
        if (res.text) {
          setInput(prev => (prev ? prev + ' ' : '') + res.text);
          // Auto-resize after inserting text
          setTimeout(() => {
            if (inputRef.current) autoResize(inputRef.current);
          }, 0);
        } else {
          setError('Không nhận dạng được giọng nói. Thử lại nhé.');
        }
      } catch {
        setError('Lỗi nhận dạng giọng nói');
      } finally {
        setTranscribing(false);
      }
    } else {
      await startRecording();
    }
  };

  // ── All sections done ──────────────────────────────────────────────────
  if (isAllDone) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#e8f2fd] via-white to-[#f0f4ff] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl shadow-blue-100 w-full max-w-md overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-green-400 to-emerald-500" />
          <div className="px-8 py-10 text-center">
            <div className="w-16 h-16 rounded-2xl bg-green-500 flex items-center justify-center mx-auto shadow-lg shadow-green-200 mb-5">
              <Sparkles size={28} className="text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Chatbot đã sẵn sàng! 🎉</h2>
            <p className="text-sm text-gray-500 mb-6">
              Đã thu thập đủ thông tin để chatbot hoạt động thông minh cho doanh nghiệp của bạn.
            </p>
            <button
              onClick={onComplete}
              className="w-full py-3 rounded-xl bg-[#1877F2] text-white font-bold text-sm hover:bg-[#166fe5] transition-all shadow-md shadow-blue-200"
            >
              Đến trang quản lý Chatbot →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Main interview UI
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#e8f2fd] via-white to-[#f0f4ff] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl shadow-blue-100 w-full max-w-lg overflow-hidden flex flex-col" style={{ maxHeight: '90vh' }}>
        {/* Top accent */}
        <div className="h-1.5 bg-gradient-to-r from-[#1877F2] to-[#42a5f5]" />

        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1877F2] to-[#42a5f5] flex items-center justify-center shadow-md shadow-blue-200">
              <Bot size={20} className="text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-bold text-gray-900">Thiết lập Chatbot AI</h2>
              <p className="text-xs text-gray-400">Trả lời để chatbot hiểu doanh nghiệp của bạn</p>
            </div>
            <button
              onClick={skipAll}
              className="text-xs text-gray-400 hover:text-gray-600 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Bỏ qua tất cả
            </button>
            <button
              onClick={() => { if (!ttsEnabled) { setTtsEnabled(true); } else { stopSpeaking(); setTtsEnabled(false); } }}
              className={`p-1.5 rounded-lg transition-colors ${ttsEnabled ? 'text-blue-500 hover:bg-blue-50' : 'text-gray-300 hover:bg-gray-50'}`}
              title={ttsEnabled ? 'Tắt đọc câu hỏi' : 'Bật đọc câu hỏi'}
            >
              {ttsEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </button>
            {ttsEnabled && voices.length > 0 && (
              <select
                value={selectedVoiceURI}
                onChange={e => setSelectedVoiceURI(e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1 text-gray-600 bg-white outline-none focus:border-blue-300 max-w-[130px] cursor-pointer"
                title="Chọn giọng đọc"
              >
                {voices.map(v => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Section progress */}
          <div className="flex gap-2">
            {SECTIONS.map((s, i) => {
              const isDone = completedSections.has(s.key);
              const isCurrent = i === sectionIdx;
              return (
                <div
                  key={s.key}
                  className={`flex-1 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all ${
                    isDone
                      ? 'bg-green-50 text-green-700 border border-green-200'
                      : isCurrent
                      ? 'bg-blue-50 text-blue-700 border border-blue-200'
                      : 'bg-gray-50 text-gray-400 border border-gray-100'
                  }`}
                >
                  {isDone ? (
                    <Check size={13} className="flex-shrink-0" />
                  ) : (
                    <span className="flex-shrink-0">{s.icon}</span>
                  )}
                  <span className="truncate">{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Chat area */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3" style={{ minHeight: '250px' }}>
          {/* Section intro */}
          <div className="text-center mb-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-medium">
              {currentSection.icon}
              {currentSection.label}
            </span>
          </div>

          {history.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  msg.role === 'user'
                    ? 'bg-[#1877F2] text-white rounded-br-md'
                    : 'bg-gray-100 text-gray-800 rounded-bl-md'
                }`}
              >
                {msg.content}
                {msg.role === 'assistant' && (
                  <button
                    onClick={() => speakingIdx === i ? stopSpeaking() : speakText(msg.content, i)}
                    className={`inline-flex items-center ml-2 align-middle opacity-40 hover:opacity-100 transition-opacity ${
                      speakingIdx === i ? 'opacity-100 text-blue-500' : ''
                    }`}
                    title="Đọc tin nhắn"
                  >
                    <Volume2 size={13} />
                  </button>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
                <Loader2 size={16} className="animate-spin text-gray-400" />
              </div>
            </div>
          )}

          {saving && (
            <div className="flex justify-center">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 text-green-600 text-xs font-medium">
                <Loader2 size={12} className="animate-spin" />
                Đang lưu...
              </span>
            </div>
          )}

          {completedSections.has(currentSection.key) && sectionIdx + 1 < SECTIONS.length && (
            <div className="flex justify-center pt-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-50 text-green-600 text-xs font-medium">
                <Check size={12} />
                Đã hoàn thành — chuyển sang phần tiếp...
              </span>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        {/* Error */}
        {(error || micError) && (
          <div className="px-6 py-2">
            <p className="text-xs text-red-500 text-center">{error || micError}</p>
          </div>
        )}

        {/* Input area */}
        {!completedSections.has(currentSection.key) && (
          <div className="px-4 pb-4 pt-2 border-t border-gray-100">
            {/* Recording waveform indicator */}
            {isRecording && (
              <div className="flex items-center justify-center gap-1 mb-2 py-2">
                <div className="flex items-center gap-[3px] h-6">
                  {Array.from({ length: 20 }).map((_, i) => {
                    const h = Math.max(4, audioLevel * 24 * (0.5 + Math.sin(Date.now() / 100 + i * 0.7) * 0.5));
                    return (
                      <div
                        key={i}
                        className="w-[3px] rounded-full bg-red-400 transition-all duration-75"
                        style={{ height: `${h}px` }}
                      />
                    );
                  })}
                </div>
                <span className="text-xs text-red-500 font-medium ml-2">Đang ghi âm...</span>
              </div>
            )}

            {/* Transcribing indicator */}
            {transcribing && (
              <div className="flex items-center justify-center gap-2 mb-2 py-2">
                <Loader2 size={14} className="animate-spin text-blue-500" />
                <span className="text-xs text-blue-500 font-medium">Đang nhận dạng giọng nói...</span>
              </div>
            )}

            <div className="flex gap-2 items-end">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => { setInput(e.target.value); autoResize(e.target); }}
                onKeyDown={handleKeyDown}
                placeholder={isRecording ? 'Đang ghi âm...' : 'Nhập câu trả lời... (Ctrl+Enter để xuống dòng)'}
                disabled={loading || saving || isRecording || transcribing}
                rows={1}
                className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300 disabled:opacity-50 transition-all resize-none overflow-hidden"
                style={{ maxHeight: '120px' }}
              />
              {/* Mic button */}
              <button
                onClick={handleVoiceToggle}
                disabled={loading || saving || transcribing}
                className={`px-3 py-2.5 rounded-xl transition-all shadow-sm ${
                  isRecording
                    ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                } disabled:opacity-40 disabled:cursor-not-allowed`}
                title={isRecording ? 'Dừng ghi âm' : 'Nhập bằng giọng nói'}
              >
                {isRecording ? <Square size={16} /> : <Mic size={16} />}
              </button>
              {/* Send button */}
              <button
                onClick={sendMessage}
                disabled={!input.trim() || loading || saving || isRecording || transcribing}
                className="px-4 py-2.5 bg-[#1877F2] text-white rounded-xl hover:bg-[#166fe5] disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                <Send size={16} />
              </button>
            </div>
            <div className="flex justify-between items-center mt-2.5 px-1">
              <button
                onClick={skipSection}
                disabled={loading || saving}
                className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors disabled:opacity-50"
              >
                <SkipForward size={12} />
                Bỏ qua phần này
              </button>
              <span className="text-xs text-gray-300">
                {sectionIdx + 1} / {SECTIONS.length}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingInterview;
